<template>
  <Loading v-if="loading" />
  <div class="sidebar">
    <div class="sidebar__container">
      <div
        v-if="
          user?.role.name !== 'janitor' && user?.role.name !== 'chain_admin'
        "
        class="sidebar__container-menu-head"
      >
        <div
          class="head-img"
          @click="goToPickupBox(user?.pharmacyId, pharmacy?.pickupBoxes[0])"
        >
          <img src="@/assets/icons/Apoteket.svg" alt="Apoteket" />
        </div>
        <div class="head-block" @click="goToEditPharmacy(user.pharmacyId)">
          <p class="pharmacy-name">{{ pharmacyName }}</p>
          <div class="head-block-edit">
            <img src="@/assets/icons/EditGreen.svg" alt="Edit" />
            <p>{{ $t('edit') }}</p>
          </div>
        </div>
      </div>
      <nav class="sidebar__container-menu-nav">
        <router-link
          v-for="el in filteredConfigs"
          :key="el.name"
          :class="{
            'router-link': true,
            'router-link-none': showElementsSideBar(el.translation),
            'router-link-padding': user?.role.name === 'janitor'
          }"
          :to="{ name: el.name }"
          @click="goTo(el.name)"
        >
          <img :src="require(`@/assets/icons/${el.name}.svg`)" alt="" />
          <p>{{ $t(`${el.translation}`) }}</p>
          <img
            v-if="isPending && el.translation === 'callbackRequests'"
            class="isPending"
            src="@/assets/icons/Green.svg"
            alt=""
          />
        </router-link>
        <a
          v-if="user?.role.name !== 'chain_admin'"
          class="link-userGuide"
          href="https://remomedi.com/remomedi-webtool-kayttoohjeet/"
          target="_blank"
        >
          <img :src="require(`@/assets/icons/UserGuide.svg`)" alt="" />
          <p>{{ $t('userGuide') }}</p>
        </a>
      </nav>
      <Logout />
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import 'firebase/compat/auth';

import Logout from '@/pages/Auth/Logout.vue';
import Loading from '@/views/Loading.vue';

import { routesConfig, routesConfigChain } from './configs';
import { Params } from '@/store/modules/users/types';
import { CallbackRequestsData } from '@/store/modules/callerAuthentication/types';

import { goToPickupBox } from '@/utils/goToPickupBox';
import { goToEditPharmacy } from '@/utils/goToEditPharmacy';

@Options({
  components: {
    Logout,
    Loading
  },
  data() {
    return {
      loading: false,
      routesConfig,
      routesConfigChain,
      pharmacyName: '',
      isPending: false
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    ...mapState('callerAuthentication', ['callbackRequests']),
    filteredConfigs() {
      if (this.user?.role.name === 'chain_admin') {
        return routesConfigChain;
      } else {
        return routesConfig;
      }
    }
  },
  created() {
    this.show();
  },
  methods: {
    ...mapActions('account', ['getUserProfile']),
    ...mapActions('pharmacy', ['getPharmacy']),
    ...mapActions('callerAuthentication', ['getCallbackRequests']),
    async show() {
      this.loading = true;

      if (
        window.location.pathname !== '/' &&
        window.location.pathname !== '/auth/login' &&
        window.location.pathname !== '/payments/success' &&
        window.location.pathname !== '/payments/failure' &&
        window.location.pathname !== '/link/apps' &&
        window.location.pathname !== '/authentication/register/verified' &&
        window.location.pathname !== '/verified' &&
        window.location.pathname !== '/sms/verified' &&
        window.location.pathname !== '/authentication/re-register/verified' &&
        window.location.pathname !== '/auth/changePharmacy' &&
        window.location.pathname !== '/auth/forgotPassword' &&
        window.location.pathname !== '/404' &&
        window.location.pathname !== '/locationNotAllowed' &&
        window.location.pathname !== '/:pathMatch(.*)*' &&
        window.location.pathname !== '/pickup-pin-qr' &&
        !window.location.pathname.startsWith('/link/pharmacy-id/') &&
        !/^\/verify\/[^/]+\/[^/]+$/.test(window.location.pathname) &&
        !window.location.pathname.startsWith('/authenticate-sms') &&
        !window.location.pathname.startsWith('/pay')
      ) {
        const userRole = this.user?.role.name;

        await this.getUserProfile();

        if (userRole !== 'janitor' && userRole !== 'chain_admin') {
          await this.getPharmacy(this.user.pharmacyId);
        }
      }

      this.loading = false;
    },
    async getNewCallbackRequests(params: Params) {
      let defaultParams = {
        lastTimestamp: this.lastTimestamp,
        pharmacyId: this.pharmacy.id
      };

      if (params) {
        defaultParams = {
          ...defaultParams,
          ...(params.lastTimestamp !== undefined && {
            lastTimestamp: params.lastTimestamp
          }),
          ...(params.pharmacyId !== undefined && {
            pharmacyId: params.pharmacyId
          })
        };
      }

      const userRole = this.user?.role.name;

      if (userRole !== 'janitor') {
        await this.getCallbackRequests(defaultParams);

        const hasPending = this.callbackRequests.some(
          (item: CallbackRequestsData) => item.status === 'pending'
        );

        if (hasPending) {
          return (this.isPending = true);
        }
      }
    },
    showElementsSideBar(name: string) {
      const { remomediServices } = this.pharmacy || {};

      const boxId = this.pharmacy?.pickupBoxes[0];
      const userRole = this.user?.role.name;

      const isJanitor = 'janitor';
      const isChain = 'chain_admin';

      const conditions: Record<string, unknown> = {
        callerAuthentication:
          !remomediServices?.mobileApp || userRole === isJanitor,
        authenticationHistory:
          !remomediServices?.mobileApp || userRole === isJanitor,
        ordersHistory: !remomediServices?.mobileApp || userRole === isJanitor,
        callbackRequests:
          !remomediServices?.mobileApp || userRole === isJanitor,
        pickupBoxes:
          !remomediServices?.pickupbox || !boxId || userRole === isJanitor,
        sensors: !remomediServices?.sensors,
        users:
          userRole === isChain
            ? false
            : userRole !== 'main_user' || userRole === isJanitor,
        customersFeedback:
          userRole === isChain
            ? false
            : !remomediServices?.feedback || userRole === isJanitor
      };

      return conditions[name] ?? false;
    },
    goTo(name: string) {
      if (name === 'PickupBoxes') {
        goToPickupBox(this.user?.pharmacyId, this.pharmacy?.pickupBoxes[0]);
      }
    },
    goToPickupBox,
    goToEditPharmacy
  },
  watch: {
    pharmacy() {
      if (this.pharmacy) {
        this.pharmacyName = this.pharmacy?.name;

        this.getNewCallbackRequests();
      }
    }
  }
})
export default class Sidebar extends Vue {}
</script>

<style lang="scss">
.sidebar {
  max-width: 208px;
  min-width: 208px;
  height: 100vh;
  border-right: 1px solid $active;
  .sidebar__container {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    background-color: $light-grey;
    outline: none !important;
  }
}

.sidebar__container-menu-head {
  display: flex;
  align-items: center;
  padding: 24px;
  padding-bottom: 0;
  background-color: $light-grey;
  .head-img {
    width: 42px;
    height: 40px;
    margin-right: 14px;
    border: 1px solid $active;
    border-radius: 7px;
    text-align: center;
    background: $white;
    cursor: pointer;
    img {
      margin: 11px;
    }
  }
  .head-block {
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: $light-grey;
    cursor: pointer;
    p {
      font-size: $fourteenSize;
      line-height: $fourteenSize;
      font-family: 'Ubuntu-Medium';
      background-color: $light-grey;
    }
    .head-block-edit {
      display: flex;
      align-items: center;
      margin-top: 6px;
      background-color: $light-grey;
      cursor: pointer;
      img {
        background-color: $light-grey;
      }
      p {
        margin-left: 5px;
        color: $main-green;
      }
    }
  }
}

.sidebar__container-menu-nav {
  display: flex;
  flex-direction: column;
  background-color: $light-grey;
  .router-link {
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    background-color: $light-grey;
    &.router-link-none {
      display: none;
    }
    &:first-child {
      margin-top: 10px;
      margin-bottom: 50px;
    }
    &.router-link-padding {
      margin-top: 75px;
    }
    &.router-link-active,
    &.router-link-active p,
    &.router-link-active img {
      background-color: $active;
    }
    img {
      width: 15px;
      height: 15px;
      margin: 0 12px 0 23px;
      background-color: $light-grey;
    }
    .isPending {
      width: 8px;
      height: 8px;
      margin: 0 12px 0 12px;
    }
    p {
      font-size: $fourteenSize;
      line-height: $fourteenSize;
      font-family: 'Ubuntu-Regular';
      background-color: $light-grey;
    }
    &:hover {
      background-color: $active;
    }
    &:hover p {
      background-color: $active;
    }
    &:hover img {
      background-color: $active;
    }
  }
  .link-userGuide {
    display: flex;
    align-items: center;
    width: 100%;
    height: 48px;
    background-color: $light-grey;
    cursor: pointer;
    img {
      width: 15px;
      height: 15px;
      margin: 0 12px 0 23px;
      background-color: $light-grey;
    }
    p {
      font-size: $fourteenSize;
      line-height: $fourteenSize;
      font-family: 'Ubuntu-Regular';
      background-color: $light-grey;
    }
    &:hover {
      background-color: $active;
    }
    &:hover p {
      background-color: $active;
    }
    &:hover img {
      background-color: $active;
    }
  }
  .router-link-exact-active {
    background-color: $active;
    p,
    img {
      background-color: $active;
    }
  }
}
</style>
